import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';

// Components
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/common/ProtectedRoute';
import LoginPage from './pages/auth/LoginPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import UsersPage from './pages/users/UsersPage';
import WorkTimingsPage from './pages/users/WorkTimingsPage';
import UserProfilePage from './pages/users/UserProfilePage';
import UserAttendancePage from './pages/users/UserAttendancePage';
import ClustersPage from './pages/clusters/ClustersPage';
import StoresPage from './pages/stores/StoresPage';
import StoreDetailsPage from './pages/stores/StoreDetailsPage';
import StoreManagerManagementPage from './pages/stores/StoreManagerManagementPage';
import ShiftsPage from './pages/shifts/ShiftsPage';
import AttendancePage from './pages/attendance/AttendancePage';
import LeavesPage from './pages/leaves/LeavesPage';
import VisitorsPage from './pages/visitors/VisitorsPage';
import MeetingsPage from './pages/meetings/MeetingsPage';
import SettingsPage from './pages/settings/SettingsPage';
import VehicleCapturesPage from './pages/captures/VehicleCapturesPage';
import TemperatureCapturesPage from './pages/captures/TemperatureCapturesPage';
import PerformersPage from './pages/performers/PerformersPage';
import AnnouncementsPage from './pages/announcements/AnnouncementsPage';
import ContactsPage from './pages/contacts/ContactsPage';
import VendorsPage from './pages/vendors/VendorsPage';

// Hooks
import { useAuth } from './hooks/useAuth';

// Constants
import { ROUTES } from './constants';

// Theme
import theme from './theme';
// ... existing code ...

const App: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <Routes>
          {/* Public Routes */}
          <Route
            path={ROUTES.LOGIN}
            element={
              isAuthenticated ? (
                <Navigate to={ROUTES.DASHBOARD} replace />
              ) : (
                <LoginPage />
              )
            }
          />

          {/* Protected Routes */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <Routes>
                    <Route path={ROUTES.DASHBOARD} element={<DashboardPage />} />
                    <Route path={ROUTES.USERS} element={<UsersPage />} />
                    <Route path={ROUTES.WORK_TIMINGS} element={<WorkTimingsPage />} />
                    <Route path="/users/:userId" element={<UserProfilePage />} />
                    <Route path="/users/:userId/attendance" element={<UserAttendancePage />} />
                    <Route path={ROUTES.CLUSTERS} element={<ClustersPage />} />
                    <Route path={ROUTES.STORES} element={<StoresPage />} />
                    <Route path={ROUTES.STORE_MANAGER_MANAGEMENT} element={<StoreManagerManagementPage />} />
                    <Route path={ROUTES.SHIFTS} element={<ShiftsPage />} />
                    <Route path={ROUTES.ATTENDANCE} element={<AttendancePage />} />
                    <Route path={ROUTES.LEAVES} element={<LeavesPage />} />
                    <Route path={ROUTES.VISITORS} element={<VisitorsPage />} />
                    <Route path={ROUTES.MEETINGS} element={<MeetingsPage />} />
                    <Route path={ROUTES.SETTINGS} element={<SettingsPage />} />
                    <Route path={ROUTES.PERFORMERS} element={<PerformersPage />} />
                    <Route path={ROUTES.VEHICLE_CAPTURES} element={<VehicleCapturesPage />} />
                    <Route path={ROUTES.TEMPERATURE_CAPTURES} element={<TemperatureCapturesPage />} />
                    <Route path={ROUTES.ANNOUNCEMENTS} element={<AnnouncementsPage />} />
                    <Route path={ROUTES.CONTACTS} element={<ContactsPage />} />
                    <Route path={ROUTES.VENDORS} element={<VendorsPage />} />
                    <Route path="/stores/:storeId" element={<StoreDetailsPage />} />
                    <Route path="/" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
                    {/* Add more protected routes here */}
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

export default App;